import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Box,
  Tooltip,
  Button,
  Checkbox,
  Typography,
  Stack,
  Switch,
} from "@mui/material";

import TrashImage from "../../Resources/CommonTable/delete.png";
import BlockImage from "../../Resources/CommonTable/Block.png";
import CrossImage from "../../Resources/CommonTable/cross.png";
import VisitURLImg from "../../Resources/CommonTable/url visit.png";
import DisableSwitchImg from "../../Resources/CommonTable/disable switch.png";
import EnableSwitchImg from "../../Resources/CommonTable/enable switch.png";
import EditImage from "../../Resources/CommonTable/edit.png";
import ViewImage from "../../Resources/CommonTable/view.png";
import MappingImage from "../../Resources/CommonTable/Mapping.png";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Styles from "./MasterCommonTable.module.scss";
import { visuallyHidden } from "@mui/utils";
import moment from "moment";
import Nodata from "../../../src/Resources/Images/NoDataNavy.png";
import DynamicActions from "./DynamicAction";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CommonLoader from "../CommonLoader/CommonLoader";
import MasterHistoryButton from "../MasterHistory/MasterHistoryButton";

const MasterCommonTable = ({
  columns,
  data,
  allData,
  handleEdit,
  handleView,
  handleMapping,
  handleApprove,
  handleCancel, // opt-in: tableConfig.isCancel shows a "Cancel Policy" icon on approved rows for the admin login
  handleDelete,
  handleMultiDelete,
  handleOnClickBehaviour,
  handleButton,
  loading,
  handleUpload,
  handleDownload,
  handleButton2,
  handleAutoFillToggle,
  tableConfig,
  tableActionButton,
  openLoader,
  tableTotalCount = 0,
  isServerSide = false,
  dashboard = false,
  limit,
  tableOutputEvent = () => { },
  paginationReset, // New prop to trigger page reset
  rowsPerPageOptions = [5, 10, 25, 50, 100], // a { label: "All", value: -1 } option shows every row
}) => {

  const isAdminLogin = String(sessionStorage.getItem("userType") || "")
    .split(",")
    .map((type) => type.trim())
    .includes("CLIENT");
  const [order, setOrder] = React.useState("");
  const [orderBy, setOrderBy] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(limit || 10);
  const [showNoData, setShowNoData] = useState(false);
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const [sortedData, setSortedData] = React.useState(null);
  const [selectedItems, setSelectedItems] = React.useState([]);

  // Reset page when paginationReset prop changes
  useEffect(() => {
    setPage(0);
  }, [paginationReset]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    // Debug: Check if data has isAutoFill when sorting by it
    // if (orderBy === 'isAutoFill' && data?.length > 0) {
    //   console.log('Data received by table:', {
    //     totalItems: data.length,
    //     first3Items: data.slice(1676, 3).map(d => ({
    //       id: d.userIdNumber,
    //       isAutoFill: d.isAutoFill,
    //       hasField: 'isAutoFill' in d,
    //       allKeys: Object.keys(d)
    //     }))
    //   });
    // }

    if (!isServerSide) {
      // The rows changed (a search, a tab): never stay on a page past the last one
      if (data && page * rowsPerPage >= data.length) {
        setPage(0);
      }
      const sorted = stableSort(data, getComparator(order, orderBy));
      setSortedData(sorted);
    } else {
      setSortedData(data);
      if (data?.length === 0 && tableTotalCount) {
        setPage((prev) => (prev > 0 ? prev - 1 : 0));
        if (page >= 0) {
          tableOutputEvent({
            page: page || 1,
            limit: rowsPerPage,
            sortType: order,
            sort: order === "desc" ? `-${orderBy}` : orderBy,
          });
        }
      }
    }
  }, [data, order, orderBy, isServerSide]);

  const handleRequestSort = (event, property) => {
    setPage(0);
    const isAsc = orderBy === property && order === "asc";
    const orderType = isAsc ? "desc" : "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    if (isServerSide) {
      setSortedData(data);
      tableOutputEvent({
        page: 1,
        limit: rowsPerPage,
        sortType: orderType,
        sort: orderType === "desc" ? `-${property}` : property,
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isServerSide) {
      tableOutputEvent({
        page: newPage + 1,
        limit: rowsPerPage,
        sortType: order,
        sort: order === "desc" ? `-${orderBy}` : orderBy,
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (isServerSide) {
      tableOutputEvent({
        page: 1,
        limit: parseInt(event.target.value, 10),
        sortType: order,
        sort: order === "desc" ? `-${orderBy}` : orderBy,
      });
    }
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array?.map((el, index) => [el, index]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    // Handle boolean values by converting to numbers
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Check if we're comparing boolean or undefined values (for isAutoFill field)
    if (typeof aValue === 'boolean' || typeof bValue === 'boolean' ||
      (orderBy === 'isAutoFill' && (aValue === undefined || bValue === undefined))) {
      // Treat undefined as true (the default value in the model)
      aValue = aValue === undefined ? 1 : (aValue ? 1 : 0);
      bValue = bValue === undefined ? 1 : (bValue ? 1 : 0);
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  };

  const handleSelectAll = async (event) => {
    if (event.target.checked) {
      const newSelecteds = allData?.map((row) => row._id);
      setSelectedItems(newSelecteds);
      return;
    }
    setSelectedItems([]);
  };

  // tableConfig.selectAll: the header checkbox ticks every row of the list (all pages) as ROW objects,
  // the same thing a row's own checkbox keeps, so handleMultiDelete receives rows either way
  const handleSelectAllRows = (event) => {
    if (event.target.checked) {
      setSelectedItems(allData || []);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedItems.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItems, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1)
      );
    }
    setSelectedItems(newSelected);
  };

  const handleDownloadSelectAll = async (event) => {
    if (event.target.checked) {
      const newSelecteds = data?.map((row) => row._id);
      setSelectedItems(newSelecteds);
      return;
    }
    setSelectedItems([]);
  };

  const handleDownloadSelectOne = (event, id) => {
    const selectedIndex = selectedItems.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedItems, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedItems.slice(1));
    } else if (selectedIndex === selectedItems.length - 1) {
      newSelected = newSelected.concat(selectedItems.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedItems.slice(0, selectedIndex),
        selectedItems.slice(selectedIndex + 1)
      );
    }

    setSelectedItems(newSelected);
  };

  const handleDeleteSelected = (selectedItems) => {
    handleMultiDelete(selectedItems);
    // setSelectedItems([]);
  };

  const getClassName = (className, row) => {
    if (typeof className === "function") {
      return className(row);
    }
    return className;
  };

  // const RenderView = (el, column) => {

  //   console.log("elelel",el)
  //   console.log("columnskksksk",column)

  //   if (Array.isArray(el, column)) {
  //     return (
  //       <Box>
  //         {el.map((value, i) => (
  //           <Typography className={`${Styles.TableBodyCell} ${column.class ? ` ${getClassName(column.class, el)}` : null
  //             }`} key={i}>
  //             {value != null ? value : "---"}
  //           </Typography>
  //         ))}
  //       </Box>
  //     );
  //   } else {
  //     return (
  //       <>
  //         <Typography
  //           color={
  //             el === "Approved" || el === "FULL-INFO"
  //               ? "green"
  //               : el === "Pending" || el === "INPROGRESS"
  //                 ? "orange"
  //                 : el === "PARTIAL-INFO" || el === "Rejected"
  //                   ? "red"
  //                   : "black"
  //           }
  //           className={Styles.TableBodyCell}
  //         >
  //           {el != null ? el : "--"}
  //         </Typography>
  //       </>
  //     );
  //   }
  // };

  const renderView = (row, column) => {
    if (column.type === "dynamicAction" && row[column.type]) {
      return (
        <DynamicActions
          row={row}
          handleView={handleView}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleApprove={handleApprove}
        />
      );
    }

    if (column.type === "button") {
      if (column.condition && column.condition(row)) {
        return (
          <Button
            variant={column?.buttonVariant}
            color={column?.buttonColor}
            className={column?.buttonClass}
            onClick={() =>
            // handleButton[column.buttonLabel](row, column.actionType)
            {
              typeof column?.behaviour === "function"
                ? column.behaviour(row)?.(row, column?.actionType)
                : handleButton[column?.buttonLabel]?.(
                  row,
                  column?.actionType
                );
            }
            }
            endIcon={column.icon || null}
          >
            {/* {column.buttonLabel} */}
            {typeof column?.newLabel === "function"
              ? column.newLabel(row)
              : column?.buttonLabel}
          </Button>
        );
      } else if (column?.altComponent) {
        return React.createElement(column?.altComponent);
      } else {
        // Show fallback text if the button condition is not met
        return (
          <Typography className={Styles.TableBodyCell}>
            {column.fallbackText ? column.fallbackText(row) : "--"}
          </Typography>
        );
      }
    }

    // Handle switch type
    if (column.type === "switch") {
      return (
        <Switch
          checked={row[column.id] || false}
          onChange={() => handleAutoFillToggle && handleAutoFillToggle(row)}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#1E3A5F",
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#1E3A5F",
            },
          }}
        />
      );
    }

    // Handle coloredText type
    if (column.type === "coloredText") {
      const color = column.getColor ? column.getColor(row) : "inherit";
      return (
        <Typography
          className={`${Styles.TableBodyCell} ${column.class ? ` ${getClassName(column.class, row)}` : null
            }`}
          style={{ color: color, fontWeight: 500 }}
          onClick={
            column.onClick
              ? () => handleOnClickBehaviour({ _id: row._id })
              : undefined
          }
        >
          {row[column.id] != null ? row[column.id] : "--"}
        </Typography>
      );
    }

    // Handle component type
    if (column.type === "component" && column.renderComponent) {
      return column.renderComponent(row);
    }

    // Handle index type for S.No column
    if (column.type === "index") {
      return (
        <Typography className={Styles.TableBodyCell}>
          {row.__index__ != null ? row.__index__ : "--"}
        </Typography>
      );
    }

    const cellValue =
      column.id === "type"
        ? row?.typeData?.name || "--"
        : column.type === "date" && row[column.id]
          ? row?.issueDateFormate
            ? moment(row[column.id]).format(row?.issueDateFormate)
            : moment(row[column.id]).format("D-M-YY")
          : column.type === "amount" && row[column.id]
            ? Number(row[column.id]).toFixed(2)
            : column.format === "integer" && row[column.id] != null
              ? Math.round(Number(row[column.id]))
              : row[column.id] != null
                ? row[column.id]
                : "--";

    // tableConfig.wrapCells folds long text into up to 3 lines; the full text
    // is then available as the hover tooltip
    const wrapLong =
      tableConfig.wrapCells && typeof cellValue === "string" && cellValue.length > 30;
    return (
      <Typography
        className={`${Styles.TableBodyCell} ${
          tableConfig.wrapCells ? Styles.TableBodyCellWrap : ""
        } ${wrapLong ? Styles.TableBodyCellWrapLong : ""} ${column.class ? ` ${getClassName(column.class, row)}` : null
          }`}
        title={wrapLong ? cellValue : undefined}
        onClick={
          column.onClick
            ? () => handleOnClickBehaviour({ _id: row._id })
            : undefined
        }
      >
        {cellValue}
      </Typography>
    );
  };

  React.useEffect(() => {
    if (tableConfig.isMultiDelete == true) {
      handleDeleteSelected(selectedItems);
    }
  }, [selectedItems]);

  React.useEffect(() => {
    if (tableConfig.isDownload == true) {
      handleDeleteSelected();
    }
  }, [selectedItems]);

  React.useEffect(() => {
    setSelectedItems([]);
  }, [allData]);

  React.useEffect(() => {
    let timer;
    if (!loading && sortedData && sortedData.length === 0) {
      timer = setTimeout(() => {
        setShowNoData(true);
      }, 100); // 100ms delay
    } else {
      setShowNoData(false);
    }
    return () => clearTimeout(timer);
  }, [loading, sortedData]);

  return (
    <div className={Styles.renderingTable}>
      <TableContainer
        className={Styles.tableContainer}
        sx={{
          height: dashboard ? "40vh !important" : "",
          overflowX: "auto !important",
          width: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 200px)", // Adjust based on your header/footer height
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Box className={Styles.tableContent}>
            <Table
              stickyHeader
              className={sortedData?.length ? "" : "h-100"}
              style={{ minWidth: "1200px" }}
            >
              <TableHead>
                <TableRow>
                  {tableConfig.isMultiDelete && (
                    <TableCell align="center" className={Styles.TableCell}>
                      {tableConfig.selectAll && (
                        <Checkbox
                          color="primary"
                          indeterminate={
                            selectedItems?.length > 0 &&
                            selectedItems?.length < allData?.length
                          }
                          checked={
                            allData?.length > 0 &&
                            selectedItems?.length === allData?.length
                          }
                          onChange={handleSelectAllRows}
                        />
                      )}
                      {/* <Checkbox
                          color="primary"
                          indeterminate={
                            selectedItems?.length > 0 &&
                            selectedItems?.length < allData?.length
                          }
                          checked={
                            allData?.length > 0 &&
                            selectedItems?.length === allData?.length
                          }
                          onChange={handleSelectAll}
                        /> */}
                      {/* {selectedItems.length > 0 && (
                          <Box>
                            <Tooltip title="Delete">
                              <button
                                onClick={() => handleDeleteSelected()}
                                style={{
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  cursor: "pointer",
                                }}
                              >
                                <img
                                  src={TrashImage}
                                  alt="trash-icon"
                                  style={{ display: "inline" }}
                                />
                              </button>
                            </Tooltip>
                          </Box>
                        )} */}
                    </TableCell>
                  )}
                  {tableConfig.isDownload && (
                    <TableCell align="center">
                      <Checkbox
                        color="primary"
                        indeterminate={
                          selectedItems?.length > 0 &&
                          selectedItems?.length < data?.length
                        }
                        checked={
                          data?.length > 0 &&
                          selectedItems?.length === data?.length
                        }
                        onChange={handleDownloadSelectAll}
                      />
                    </TableCell>
                  )}
                  {columns?.map((column) => (
                    <>
                      {column?.id !== "isApplicable" ? (
                        <>
                          <TableCell
                            key={column.id}
                            sortDirection={
                              orderBy === column.id ? order : false
                            }
                            className={Styles.TableCell}
                            align="left"
                          >
                            <TableSortLabel
                              active={orderBy === column.id}
                              direction={orderBy === column.id ? order : "asc"}
                              onClick={(e) => handleRequestSort(e, column.id)}
                            >
                              {column.label}
                              {orderBy === column.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                  {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                                </Box>
                              ) : (
                                <></>
                              )}
                            </TableSortLabel>
                          </TableCell>
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  ))}
                  {tableConfig.isAction ? (
                    <TableCell className={Styles.TableCell} align="center">
                      Action
                    </TableCell>
                  ) : tableConfig?.isOption ? (
                    <TableCell className={Styles.TableCell} align="center">
                      Options
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        columns?.length +
                        (tableConfig.isAction || tableConfig?.isOption
                          ? 1
                          : 0) +
                        (tableConfig.isMultiDelete ? 1 : 0) +
                        (tableConfig.isDownload ? 1 : 0)
                      }
                      align="center"
                      sx={{ border: "none", height: "300px" }}
                    >
                      <Box className={Styles.loaderContainer}>
                        <CommonLoader />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : sortedData?.length ? (
                  (isServerSide
                    ? sortedData
                    : rowsPerPage === -1
                      ? sortedData
                      : sortedData.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                  )?.map((row, index) => {
                    // console.log("index --", index);
                    const isSelected = selectedItems.some((item) => {
                      if (tableConfig.type === "userWallet") {
                        return item.userId === row.userId;
                      } else if (tableConfig.type === "branchWallet") {
                        return item._id === row._id;
                      } else if (tableConfig.type === "userConfiguration") {
                        return item._id === row._id;
                      } else if (tableConfig.type === "masterReport") {
                        return item.policyNumber === row.policyNumber;
                      }
                      return false;
                    });
                    return (
                      <TableRow
                        hover
                        key={index}
                        // opt-in: tableConfig.onRowClick(row) opens the row's details (RoadZen Wallet statement); no click otherwise
                        onClick={tableConfig.onRowClick ? () => tableConfig.onRowClick(row) : undefined}
                        // opt-in: tableConfig.rowSx(row) colours a row (Online Policy's status colours); white otherwise
                        sx={{
                          backgroundColor: "white",
                          ...(tableConfig.onRowClick ? { cursor: "pointer" } : null),
                          ...(tableConfig.rowSx ? tableConfig.rowSx(row) : null),
                        }}
                      >
                        {tableConfig.isMultiDelete && (
                          <TableCell align="center" padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isSelected}
                              onChange={(event) => handleSelectOne(event, row)}
                            />
                          </TableCell>
                        )}
                        {tableConfig.isDownload && (
                          <TableCell align="center" padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isSelected}
                              onChange={(event) =>
                                handleDownloadSelectOne(event, row._id)
                              }
                            />
                          </TableCell>
                        )}
                        {columns?.map((column) => (
                          <TableCell
                            key={column.id}
                            className={`${Styles.TableDataCell} ${
                              tableConfig.wrapCells ? Styles.TableBodyCellWrap : ""
                            }`}
                          >
                            {/* {RenderView(row[column.id])} */}
                            {renderView(row, column)}
                          </TableCell>
                        ))}
                        {tableConfig.isAction || tableConfig?.isOption ? (
                          <TableCell className={Styles.TableDataCell}>
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="center"
                              alignItems="center"
                            >
                              {tableConfig.isView && (
                                <Tooltip title="View">
                                  <Box
                                    onClick={() => handleView(row)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={ViewImage}
                                      alt="view-icon"
                                      height="16px"
                                      width="16px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isViewURL && (
                                <Tooltip title="Visit URL">
                                  <Box
                                    onClick={() => window.open(row?.url)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={VisitURLImg}
                                      alt="visit-url-icon"
                                      height="20px"
                                      width="20px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isEdit && (
                                <Tooltip title="Edit">
                                  <Box
                                    onClick={() => handleEdit(row, index)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={EditImage}
                                      alt="edit-icon"
                                      height="16px"
                                      width="16px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.historyEntity && row?._id && (
                                <MasterHistoryButton
                                  entityType={tableConfig.historyEntity}
                                  entityId={row._id}
                                />
                              )}

                              {tableConfig.isMapping && (
                                <Tooltip title="Mapping">
                                  <Box
                                    onClick={() => handleMapping(row, index)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={MappingImage}
                                      alt="mapping-icon"
                                      height="16px"
                                      width="16px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isUpload && (
                                <Tooltip title="Upload">
                                  <Box
                                    onClick={() => handleUpload(row, index)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <FileUploadIcon
                                      sx={{ width: "16px", color: "#1E3A5F" }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isDelete && (
                                <Tooltip title="Delete">
                                  <Box
                                    onClick={() => handleDelete(row, index)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={TrashImage}
                                      alt="delete-icon"
                                      height="16px"
                                      width="16px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isFileDownload && (
                                <Tooltip title="Download">
                                  <Box
                                    onClick={() => handleDownload(row, index)}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <DownloadIcon
                                      sx={{ width: "16px", color: "#1E3A5F" }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isApprove && (
                                <Tooltip title="Approve">
                                  <Box
                                    onClick={() =>
                                      handleApprove(row, row.isEnabled)
                                    }
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={BlockImage}
                                      alt="approve-icon"
                                      height="16px"
                                      width="16px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isCancel && (
                                <Tooltip title="Cancel Policy">
                                  <Box
                                    onClick={() => handleCancel(row)}
                                    sx={{
                                      cursor: "pointer",
                                      width: "14px",
                                      height: "14px",
                                      display:
                                        row?.status === "approvedPolicy" &&
                                        isAdminLogin
                                          ? "flex"
                                          : "none",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={CrossImage}
                                      alt="cancel-icon"
                                      height="16px"
                                      width="16px"
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {tableConfig.isSwitch && (
                                <Tooltip
                                  title={
                                    row?.isEnabled
                                      ? "Turn Disable"
                                      : "Turn Enable"
                                  }
                                >
                                  <Switch
                                    checked={row?.isEnabled || false}
                                    onChange={() =>
                                      handleApprove(row, row.isEnabled)
                                    }
                                    size="small"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#1E3A5F",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                        backgroundColor: "#1E3A5F",
                                      },
                                      "& .MuiSwitch-track": {
                                        backgroundColor: "#98A2B3",
                                      },
                                    }}
                                  />
                                </Tooltip>
                              )}

                              {tableActionButton &&
                                tableActionButton.map((button, btnIndex) => (
                                  <Tooltip key={btnIndex} title={button.title}>
                                    <Button
                                      onClick={() => button.onClick(row)}
                                      className={Styles.actionButton}
                                      sx={{
                                        minWidth: "auto",
                                        padding: "4px",
                                      }}
                                    >
                                      {button.title}
                                    </Button>
                                  </Tooltip>
                                ))}
                            </Stack>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })
                ) : null}
                {!loading && showNoData && sortedData && sortedData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        columns?.length +
                        (tableConfig.isAction || tableConfig?.isOption
                          ? 1
                          : 0) +
                        (tableConfig.isMultiDelete ? 1 : 0) +
                        (tableConfig.isDownload ? 1 : 0)
                      }
                      align="center"
                      sx={{ border: "none", height: "300px" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={Nodata}
                          alt="No Data Found"
                          style={{
                            width: "300px",
                            height: "200px",
                            marginLeft: "20px",
                            objectFit: "contain",
                          }}
                        />
                        <Typography
                          color="#1E3A5F"
                          sx={{
                            fontSize: "22px",
                            fontFamily: "Inter",
                            textAlign: "center",
                            fontWeight: 600,
                          }}
                        >
                          No data available
                        </Typography>
                        <Typography
                          color="#64748B"
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            textAlign: "center",
                          }}
                        >
                          There is no available data to show
                        </Typography>
                        <Typography
                          color="#64748B"
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Inter",
                            textAlign: "center",
                          }}
                        >
                          Try adjusting your filters or check back later
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
        <Box
          className={Styles.PaginationContainer}
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#f9fafb",
            borderTop: "1px solid #e0e0e0",
            zIndex: 10,
          }}
        >
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={isServerSide ? tableTotalCount : data?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </TableContainer>
    </div>
  );
};

export default MasterCommonTable;
